import { Labels } from '../../global/labels.js';
import { UIElement } from '../ui-element.js';
import {
    closestByCond,
    dispatchCustomEvent,
    isParentOf,
    isVisible,
    updateElement,
    PrimaryInteractiveElements,
    SecondaryInteractiveElements,
    hasNodeOrTextChildren,
} from '../../global/ui-helpers.js';
import { TabIndex } from '../../global/keyboard.js';
import { UIIcon } from '../icon/ui-icon.js';
import { browser } from '../../global/browser.js';
import { throttle, waitForAnimations } from '../../global/helpers.js';
import { SwipeObserver } from '../../global/swipe.js';
import { Digest } from '../../global/index.js';
import { ComponentState } from '../ui-element.const.js';
import styles from './ui-modal.css';

/**
 * @memberof SharedComponents
 * @augments {UIElement}
 * @implements {Activatable}
 * @alias UIModal
 * @element ui-modal
 * @classdesc Represents a class for <code>ui-modal</code> element
 * @fires event:modal-open-enter
 * @fires event:modal-close
 * @fires event:modal-close-enter
 * @fires event:modal-action
 * @fires event:modal-updatelist
 * @property {("page"|"alert"|"confirmation"|"popover"|"bubble"|"element"|"menu")} type
 * {@attr type} The type of modal window.
 *  {@desc page: common page modal}
 *  {@desc alert: alert modal}
 *  {@desc confirmation: modal with yes/no actions}
 *  {@desc popover: represents popover element like tooltip or annotation}
 *  {@desc bubble: represents modal as bubble such as toast or notification}
 *  {@desc element: uses element as modal}
 *  {@desc menu: overflow menu modal}
 * @property {boolean} [detached] {@attr detached} Modal will be added to
 * the end of the body and after close will be returned.
 * @property {("default" | "fluid" | "logout")} [layout] {@attr layout} Layout of the modal dialog window.
 *  {@desc fluid: fluid modal layout, stretched to the size of content}
 *  {@desc logout: Used only for logout windows.}
 * @property {boolean} [compact] {@attr compact} Indicate should the cross button be hidden
 * @property {boolean} [shifted] {@attr shifted} Indicate should the modal be shifted
 * (Is used for multiple windows).
 * @property {boolean} [dynamic] {@attr dynamic}
 * @property {boolean} [closable] {@attr closable} Defines if modal window could be closed using
 * by clicking on overlay.
 * @property {boolean} [noclosable] {@attr noclosable} Opposite of closable.
 * @property {string} [modalTitle] {@attr modal-title} The title in the modal header.
 * @property {string} [modalSubtitle] {@attr modal-subtitle}The subtitle in the modal header.
 * @property {boolean} [animation] {@attr animation} true - to force animation to be played
 * @property {string} [labelAccept="Yes"] {@attr label-accept} text label for the accept button
 * (Is used for confirmation windows).
 * @property {string} [labelDecline="No"] {@attr label-decline} text label for the decline button
 * (Is used for confirmation windows).
 * @property {string} [labelDismiss="Ok"] {@attr label-dismiss} text label for the dismiss button
 * (Is used for alerts).
 * @property {boolean} [bottomSheet] {@attr bottom-sheet} Modals opens from the bottom on mobile
 * devices.
 * @property {number} order {@attr order} {@readonly} Z-order of the modal, this property
 * is set automatically.
 * @property {boolean} opened {@attr opened} Sets this attribute to open or close the modal.
 * @property {HTMLDivElement} backdrop {@readonly}
 * @property {HTMLButtonElement} closeAction {@readonly}
 * @property {UIButtonbar} buttonBar {@readonly}
 * @property {HTMLDivElement} modalWindow {@readonly}
 * @property {HTMLElement} modalHeader {@readonly}
 * @property {HTMLDivElement} modalBody {@readonly}
 * @property {HTMLElement} modalFooter {@readonly}
 * @property {HTMLElement} modalTitles {@readonly}
 * @property {HTMLElement} modalTitleElement {@readonly}
 * @property {HTMLElement} modalSubtitleElement {@readonly}
 * @property {HTMLHeadingElement} heading {@readonly}
 * @slot
 * @example
 * <div>
 *   <ui-modal id="super-modal">
 *     <h2>I am modal title</h2>
 *     <p>How are you?</p>
 *   </ui-modal>
 *   <button class="button" onclick="SharedComponents.eventBus.dispatchCustomEvent(
 *     'modal-open', { selector: '#super-modal'});">Toggle</button>
 * </div>
 */
class UIModal extends UIElement {
    /**
     * @type {Props}
     * @readonly
     */
    static get props() {
        return {
            attributes: {
                type: String,
                align: String,
                detached: Boolean,
                order: { type: Number, default: 1 },
                compact: Boolean,
                closable: { type: Boolean, default: true },
                noclosable: Boolean,
                layout: String,
                labelAccept: String,
                labelDecline: String,
                labelDismiss: String,
                animation: Boolean,
                modalTitle: String,
                modalSubtitle: String,
                bottomSheet: Boolean,
                opened: Boolean,
            },
            children: {
                backdrop: '.ui-modal__backdrop',
                closeAction: '.ui-modal__close-action',
                buttonBar: '.ui-modal__buttons',
                modalWindow: '.ui-modal__window',
                modalHeader: '.ui-modal__header',
                modalBody: '.ui-modal__body',
                modalFooter: '.ui-modal__footer',
                modalTitles: '.ui-modal__titles',
                modalTitleElement: '.ui-modal__title',
                modalSubtitleElement: '.ui-modal__subtitle',
                heading: '.ui-modal__body h2:first-of-type',
            },
        };
    }

    /**
     * Define labels what could be localised
     * @type {Labels}
     * @readonly
     */
    static get labels() {
        return Labels.attach('ui-modal', {
            accept: 'Yes',
            decline: 'No',
            dismiss: 'OK',
            close: 'Close',
            back: 'Back',
            opened: 'Pop-up window is opened',
            exitLabel: 'Press ESC to close',
        });
    }

    /**
     * @type {string[]}
     */
    static get observedAttributes() {
        return ['order', 'opened'];
    }

    /**
     * @private
     * @type {number}
     */
    static get DefaultZIndex() {
        return 899;
    }

    /**
     * @type {UIModalType}
     */
    static get Type() {
        return {
            Page: 'page',
            Confirmation: 'confirmation',
            Alert: 'alert',
            Popover: 'popover',
            Bubble: 'bubble',
            Element: 'element',
            Menu: 'menu',
        };
    }

    static get ScrollIncrementSettings() {
        return {
            StandardIncrement: 40,
            getPageIncrement: function (scrollableArea) {
                return scrollableArea.clientHeight - this.StandardIncrement;
            },
        };
    }

    get shifted() {
        return this.classList.contains('-shifted');
    }
    set shifted(value) {
        this.updateClassList({ '-shifted': value });
    }

    get locked() {
        return this.classList.contains('-locked');
    }
    set locked(value) {
        this.updateClassList({ '-locked': value });
    }

    get headingID() {
        if (this.heading?.id) {
            return this.heading.id;
        }
        if (!this._headingID) {
            this._headingID = `ui-modal-heading-${Digest.uuidv4()}`;
        }
        return this._headingID;
    }

    /**
     * @private
     * @type {{CSS: {Down: string, Up: string}, ThrottleTime: number, Offset: number}}
     */
    static get ContentShadow() {
        return {
            CSS: {
                Up: '-ui-modal-inner-shadow-up',
                Down: '-ui-modal-inner-shadow-down',
            },
            Offset: 15,
            ThrottleTime: 100,
        };
    }

    /**
     * @type {{Fluid: 'fluid', Logout: 'logout', Default: 'default'}}
     */
    static get Layouts() {
        return {
            Default: 'default',
            Fluid: 'fluid',
            Logout: 'logout',
        };
    }

    /**
     * Check if the current modal is active.
     * @returns {boolean}
     */
    isActive() {
        return this.classList.contains('-active');
    }

    /**
     * Check if current modal is animated. All modals windows are animatable
     * expect those who has type 'popover' (tooltips, dropdown, etc.).
     * @returns {boolean}
     */
    isAnimated() {
        return !browser.prefersReducedMotion();
    }

    /**
     * Opens modal window.
     * Use this function for opening modals with the order that equals
     * to the modals order location inside DOM or just for the only one modal on the page.
     * Use 'ui-modal-controller' with 'modal-open' event to follow the right order for several modals.
     * Try to avoid mixing 'modal-open' functionality of 'ui-modal-controller' with this.
     * @fires event:modal-open-enter Fires then modal is just starting to open.
     * @returns {UIModal}
     */
    open() {
        dispatchCustomEvent(this, 'modal-open-enter');
        if (!this.isConnected && this.hasAttribute('dynamic')) {
            document.body.append(this);
        }

        this.classList.add('-active');
        // reflow trick, do mot remove for smoother animation
        this['offsetWidth'].toString();
        this.classList.add('-presented');
        if (this.isAnimated()) {
            waitForAnimations(this.modalWindow)
                .catch(this.onOpen.bind(this))
                .finally(this.onOpen.bind(this));
        } else {
            this.onOpen();
        }
        return this;
    }

    /**
     * Closes modal window.
     * @fires event:modal-close-enter
     * @returns {UIModal}
     */
    close() {
        dispatchCustomEvent(this, 'modal-close-enter');
        this.classList.remove('-presented');
        // reflow trick, do mot remove for smoother animation
        this['offsetWidth'].toString();
        if (this.isAnimated()) {
            waitForAnimations(this.modalWindow)
                .catch(this.onClose.bind(this))
                .finally(this.onClose.bind(this));
        } else {
            this.onClose();
        }
        return this;
    }

    /**
     * Close action callback.
     * @private
     */
    handleCloseAction() {
        if (!this.closable || this.noclosable) {
            return;
        }
        dispatchCustomEvent(this, 'modal-close', { dismiss: true });
    }

    /**
     * Accept action callback.
     * @private
     */
    handleAcceptAction() {
        dispatchCustomEvent(this, 'modal-action', { type: 'accept' });
    }

    /**
     * Decline action callback.
     * @private
     */
    handleDeclineAction() {
        dispatchCustomEvent(this, 'modal-action', { type: 'decline' });
    }

    /**
     * Decline action callback.
     * @private
     */
    onOpen() {
        this.opened = true;
        this.modalFooter?.classList.toggle(
            this.constructor.ContentShadow.CSS.Up,
            this.modalBody.scrollHeight > this.modalBody.offsetHeight
        );
        this.setInitialFocus();
        dispatchCustomEvent(this, 'modal-updatelist', { action: 'add' });
    }

    /**
     * Decline action callback.
     * @private
     */
    onClose() {
        this.opened = false;
        this.classList.remove('-active');
        dispatchCustomEvent(this, 'modal-updatelist', { action: 'remove' });
        if (this.hasAttribute('dynamic')) {
            this.parentElement?.removeChild(this);
        } else {
            this.setAttributes({ style: null, order: '1' });
        }
    }

    /**
     * @returns {Array<HTMLElement>}
     */
    getInteractiveElements() {
        const targets = []
            .concat(PrimaryInteractiveElements)
            .concat(SecondaryInteractiveElements)
            .join(',');
        return [].filter.call(this.querySelectorAll(targets), isVisible);
    }

    /**
     * Checks if there is at least one interactive element.
     * @private
     * @returns {boolean}
     */
    hasInteractiveElements() {
        return this.getInteractiveElements().length > 0;
    }

    /**
     * Prevents the body scrolling for iOS devices.
     */
    disableBodyScroll() {
        const isElementHorizontallyScrolled = (element, shiftX) => {
            if (
                element.clientWidth >= element.scrollWidth ||
                (element.scrollLeft === 0 && -Math.abs(shiftX) > 0)
            ) {
                return false;
            }
            const scrolledX =
                element.scrollWidth - element.scrollLeft <= element.clientWidth;
            return !(scrolledX && shiftX < 0);
        };

        const isElementVerticallyScrolled = (element, shiftY) => {
            if (
                element.clientHeight >= element.scrollHeight ||
                (element.scrollTop === 0 && -Math.abs(shiftY) > 0)
            ) {
                return false;
            }
            const scrolledY =
                element.scrollHeight - element.scrollTop <=
                element.clientHeight;
            return !(scrolledY && shiftY < 0);
        };

        const cancelEvent = (event) => {
            if (!event.cancelable) {
                return;
            }
            try {
                event.preventDefault();
            } catch (e) {
                // no luck, scroll in progress etc...
            }
        };

        this.addEventListener(
            'touchstart',
            (event) => {
                if (event.targetTouches.length !== 1) {
                    return;
                }
                this._clientY = event.targetTouches[0].clientY;
                this._clientX = event.targetTouches[0].clientX;
            },
            {
                passive: true,
                capture: false,
            }
        );

        this.addEventListener(
            'touchmove',
            (event) => {
                if (event.targetTouches.length !== 1) {
                    return;
                }
                if (!isParentOf(this.modalBody, event.target)) {
                    cancelEvent(event);
                    return;
                }
                const shiftY = event.targetTouches[0].clientY - this._clientY;
                const shiftX = event.targetTouches[0].clientX - this._clientX;
                if (Math.abs(shiftY) >= Math.abs(shiftX)) {
                    const yScrollableTarget = closestByCond(
                        event.target,
                        (node) => {
                            return (
                                node === this.modalBody ||
                                node.clientHeight < node.scrollHeight
                            );
                        }
                    );
                    if (
                        !isElementVerticallyScrolled(yScrollableTarget, shiftY)
                    ) {
                        cancelEvent(event);
                    }
                } else {
                    const xScrollableTarget = closestByCond(
                        event.target,
                        (node) => {
                            return (
                                node === this.modalBody ||
                                node.clientWidth < node.scrollWidth
                            );
                        }
                    );
                    if (
                        !isElementHorizontallyScrolled(
                            xScrollableTarget,
                            shiftX
                        )
                    ) {
                        cancelEvent(event);
                    }
                }
            },
            {
                passive: false,
                capture: false,
            }
        );
    }

    /**
     * Changes current view to active (open) state.
     * @returns {UIModal}
     */
    activate() {
        return this.open();
    }

    /**
     * @inheritDoc
     */
    observeAttributes(name, oldValue, newValue) {
        if (newValue === oldValue) {
            return;
        }
        switch (name) {
            case 'order':
                if (this.order > 1) {
                    this.style.zIndex = String(
                        UIModal.DefaultZIndex + this.order
                    );
                }
                break;
            case 'opened':
                if (newValue !== null) {
                    // if component is not attached to DOM yet, open it
                    // asynchronously on next frame. Otherwise, just open it
                    // as normal in synchronous way.
                    const isAttachedToDOM =
                        this.state !== null ||
                        this.state === ComponentState.Hydrated;
                    if (!isAttachedToDOM) {
                        requestAnimationFrame(this.openModalByEvent.bind(this));
                    } else {
                        this.openModalByEvent();
                    }
                } else if (this.isActive()) {
                    this.closeModalByEvent();
                }
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * Opens modal by dispatching 'modal-open' event.
     * @private
     */
    openModalByEvent() {
        dispatchCustomEvent(this, 'modal-open', {
            selector: `#${this.id}`,
        });
    }

    /**
     * Closes modal by dispatching 'modal-close' event.
     * @private
     */
    closeModalByEvent() {
        dispatchCustomEvent(this, 'modal-close');
    }

    /**
     * Sets the ID of the element if not set.
     * @private
     */
    setUniqueId() {
        this.id ||= `ui-modal-${Digest.randomId()}`;
    }

    /**
     * Checks that modals is type of "popover" or "bubble", in other words
     * it is a small dialog.
     * @returns {boolean}
     */
    isPopover() {
        return [
            UIModal.Type.Popover,
            UIModal.Type.Bubble,
            UIModal.Type.Menu,
        ].includes(this.type);
    }

    /**
     * Builds modal header.
     * @private
     * @returns {ElementConfig}
     */
    buildModalHeader() {
        /** @type {ElementConfig} */
        const closeButton = {
            tagName: 'div',
            classList: {
                'ui-modal__close-action-container': true,
            },
            children: [
                !this.compact && {
                    tagName: 'button',
                    classList: {
                        'ui-modal__close-action': true,
                        '-iconed': true,
                    },
                    // TODO GUI-2909 use new ui-button element,
                    attributes: {
                        'type': 'button',
                        'aria-label': UIModal.labels.close,
                    },
                    children: [
                        {
                            tagName: 'ui-icon',
                            attributes: {
                                glyph: 'redesign-x-mark',
                                color: UIIcon.colors.DEFAULT,
                            },
                        },
                    ],
                },
            ],
        };

        /** @type {ElementConfig} */
        const titles = {
            tagName: 'div',
            classList: {
                'ui-modal__titles': true,
            },
            children: [
                this.modalTitle && {
                    tagName: 'span',
                    classList: {
                        'ui-modal__title': true,
                    },
                    children: [this.modalTitle],
                },
                this.modalSubtitle && {
                    tagName: 'span',
                    classList: {
                        'ui-modal__subtitle': true,
                    },
                    children: [this.modalSubtitle],
                },
            ],
        };

        /** @type {ElementConfig} */
        const backButton = {
            tagName: 'div',
            classList: {
                'ui-modal__back-action-container': true,
            },
            children: [
                // Temporary do not render back button,
                // There is no clear UX explanation how it supposed to be used.
                // Definitely this button is needed, but how it will work?
                // {
                //     tagName: 'button',
                //     classList: {
                //         'ui-modal__back-action': true,
                //         '-iconed': true,
                //     },
                //     attributes: {
                //         type: 'button',
                //         'aria-label': UIModal.labels.back,
                //     },
                //     children: [
                //         {
                //             tagName: 'ui-icon',
                //             attributes: {
                //                 glyph: 'redesign-navigate-back',
                //                 color: UIIcon.colors.DEFAULT,
                //             },
                //         },
                //     ],
                // },
            ],
        };

        const hasHeader =
            !this.compact || this.modalTitle || this.modalSubtitle;
        return {
            tagName: 'header',
            classList: {
                'ui-modal__header': true,
                '-blank': !hasHeader,
            },
            children: [backButton, titles, closeButton],
        };
    }

    /**
     * Builds modal body.
     * @private
     * @returns {ElementConfig}
     */
    buildModalBody() {
        return {
            tagName: 'div',
            classList: {
                'ui-modal__body': true,
            },
            attributes: {
                tabindex: '0',
            },
            children: this.childNodes,
        };
    }

    /**
     * Builds modal footer.
     * @private
     * @returns {ElementConfig}
     */
    buildModalFooter() {
        const footer = this.querySelector('footer');
        const elementConfig = {
            tagName: 'footer',
            classList: {
                'ui-modal__footer': true,
            },
        };
        if (footer) {
            elementConfig.element = footer;
        }
        return elementConfig;
    }

    /**
     * Builds modal backdrop with proper accessibility attributes.
     * @private
     * @returns {ElementConfig}
     */
    buildModalBackdrop() {
        const isClosableBackdrop =
            this.isClosable() && this.type !== UIModal.Type.Popover;

        return {
            tagName: 'div',
            classList: {
                'ui-modal__backdrop': true,
            },
            attributes: isClosableBackdrop
                ? {
                      'tabindex': '0',
                      'aria-label': `${UIModal.labels.opened}, ${UIModal.labels.exitLabel}`,
                  }
                : {
                      'aria-hidden': 'true',
                  },
        };
    }

    /**
     * Sets initial focus when modal opens
     * @private
     */
    setInitialFocus() {
        const interactiveElements = this.getInteractiveElements();
        if (this.hasInteractiveElements()) {
            interactiveElements[0].focus();
        } else {
            this.focus();
        }
    }

    /**
     * Processes swipe events for bottom sheet.
     * @private
     */
    connectSwipeEvents() {
        this.swipeButtonHandler = (e) => {
            if (e.detail.originalEvent instanceof TouchEvent) {
                dispatchCustomEvent(this, 'modal-close');
                this.close();
            }
        };
        this.pointerMoveHandler = (e) => {
            if (e.pointerType === 'touch') {
                // Prevent top swipe.
                if (e.offsetY < 0) {
                    return;
                }
                if (this.swipeObserverStart === 0) {
                    this.swipeObserverStart = e.clientY;
                }
                const delta = this.swipeObserverStart / e.clientY;
                this.modalWindow.style.setProperty(
                    '--ui-modal-swipe-offset',
                    `${Math.pow(Math.abs(delta), 1 / 3) * 100}%`
                );
            }
        };
        this.pointerUpHandler = () => {
            this.swipeObserverStart = 0;
            this.modalWindow.style.setProperty(
                '--ui-modal-swipe-offset',
                '100%'
            );
        };
        this.swipeObserverStart = 0;
        this.swipeObserver = new SwipeObserver(this);
        this.addEventListener('swipe-bottom', this.swipeButtonHandler);
        this.addEventListener('pointerup', this.pointerUpHandler);
        this.addEventListener('pointercancel', this.pointerUpHandler);
        this.addEventListener('pointermove', this.pointerMoveHandler);
    }

    /**
     * Removes all swipes events.
     * @private
     */
    disconnectSwipeEvents() {
        this.swipeObserver?.disconnect();
        this.removeEventListener('swipe-bottom', this.swipeButtonHandler);
        this.removeEventListener('pointerdown', this.pointerDownHandler);
        this.removeEventListener('pointerup', this.pointerUpHandler);
        this.removeEventListener('pointercancel', this.pointerUpHandler);
        this.removeEventListener('pointermove', this.pointerMoveHandler);
    }

    /**
     * Draws shadows for the header and footer.
     * @private
     * @param {Event} event
     */
    handleScrolledShadow(event) {
        /** @type {HTMLElement} */
        const elem = event.target;
        const isAtStart =
            elem.scrollTop > this.constructor.ContentShadow.Offset;
        const isAtEnd =
            elem.offsetHeight + elem.scrollTop >=
            elem.scrollHeight - this.constructor.ContentShadow.Offset;
        this.modalHeader?.classList.toggle(
            this.constructor.ContentShadow.CSS.Down,
            isAtStart
        );
        this.modalFooter?.classList.toggle(
            this.constructor.ContentShadow.CSS.Up,
            !isAtEnd
        );
    }

    /**
     * @private
     */
    setupA11y() {
        let hasTitle = true;
        if (this.heading) {
            this.heading.id = this.headingID;
        } else if (this.modalTitle) {
            this.modalTitleElement.id = this.headingID;
        } else if (this.modalSubtitle) {
            this.modalSubtitleElement.id = this.headingID;
        } else {
            hasTitle = false;
        }
        if (hasTitle) {
            this.setAttribute('aria-labelledby', this.headingID);
        }
    }

    /**
     * Is modal can be closed. This method is used for legacy compatibility.
     * @returns {boolean}
     */
    isClosable() {
        return this.closable && !this.noclosable;
    }

    /**
     * @inheritDoc
     */
    render() {
        this.setUniqueId();
        const hasBottomSheet =
            this.type === this.constructor.Type.Menu ||
            this.layout === this.constructor.Layouts.Logout ||
            this.getAttribute('bottom-sheet');
        this.setAttributes({
            'role': 'dialog',
            'aria-modal': 'true',
            'tabindex': TabIndex.Inactive,
            'bottom-sheet': hasBottomSheet,
        })
            .updateClassList({
                '-shifted': this.shifted,
            })
            .insertElements([
                this.buildModalBackdrop(),
                {
                    tagName: 'div',
                    classList: {
                        'ui-modal__window': true,
                        '-alert': this.type === UIModal.Type.Alert,
                        '-confirmation':
                            this.type === UIModal.Type.Confirmation,
                    },
                    children: [
                        !this.isPopover() && this.buildModalHeader(),
                        this.buildModalBody(),
                        !this.isPopover() && this.buildModalFooter(),
                    ],
                },
            ]);

        if (this.type === UIModal.Type.Alert) {
            this.setAttributes({
                role: 'alertdialog',
                layout: this.constructor.Layouts.Default,
            });
            this.modalFooter?.appendChild(
                this.createElement({
                    tagName: 'ui-buttonbar',
                    classList: {
                        'ui-modal__buttons': true,
                    },
                    children: [
                        {
                            tagName: 'button',
                            classList: { 'button': true, '-dismiss': true },
                            attributes: {
                                type: 'button',
                            },
                            children: [
                                this.labelDismiss || UIModal.labels.dismiss,
                            ],
                        },
                    ],
                })
            );
        }

        if (this.type === UIModal.Type.Confirmation) {
            this.setAttributes({
                layout: this.constructor.Layouts.Default,
            });
            this.modalFooter?.appendChild(
                this.createElement({
                    tagName: 'ui-buttonbar',
                    classList: {
                        'ui-modal__buttons': true,
                    },
                    children: [
                        {
                            tagName: 'button',
                            classList: {
                                'button': true,
                                '-destructive': true,
                                '-decline': true,
                            },
                            attributes: {
                                type: 'button',
                            },
                            children: [
                                this.labelDecline || UIModal.labels.decline,
                            ],
                        },
                        {
                            tagName: 'button',
                            classList: {
                                'button': true,
                                '-accept': true,
                            },
                            attributes: {
                                type: 'button',
                            },
                            children: [
                                this.labelAccept || UIModal.labels.accept,
                            ],
                        },
                    ],
                })
            );
        }

        // When we do officially support :has() selector, here is dirty workaround.
        // This can be bravely removed and make minor changes in CSS also.
        const possibleBlankElements = [
            this.modalFooter,
            ...(this.modalFooter?.querySelectorAll('ui-buttonbar > *') || []),
            this.modalTitles,
        ];
        possibleBlankElements.forEach((node) => {
            node?.classList.toggle('-blank', !hasNodeOrTextChildren(node));
        });
        this.classList.toggle(
            '-has-blank-footer',
            this.modalFooter?.classList.contains('-blank')
        );
        this.setupA11y();
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.handleCloseAction = this.handleCloseAction.bind(this);
        this.handleAcceptAction = this.handleAcceptAction.bind(this);
        this.handleDeclineAction = this.handleDeclineAction.bind(this);
        this.scrollShadowHander = this.isAnimated()
            ? throttle(
                  this.handleScrolledShadow.bind(this),
                  this.constructor.ContentShadow.ThrottleTime
              )
            : this.handleScrolledShadow.bind(this);

        if (this.type !== UIModal.Type.Popover) {
            this.handleBackdropInteraction = (event) => {
                if (
                    event.type === 'click' ||
                    (event.type === 'keydown' && event.key === 'Enter')
                ) {
                    if (event.type === 'keydown') {
                        event.preventDefault();
                    }
                    this.handleCloseAction();
                }
            };
            this.backdrop.addEventListener(
                'click',
                this.handleBackdropInteraction
            );
            this.backdrop.addEventListener(
                'keydown',
                this.handleBackdropInteraction
            );
        }

        if (this.closeAction) {
            updateElement(this.closeAction, {
                events: {
                    click: this.handleCloseAction,
                },
            });
        }

        if (this.type === UIModal.Type.Alert) {
            updateElement(this.buttonBar.querySelector('button.-dismiss'), {
                events: {
                    click: this.handleCloseAction,
                },
            });
        }

        if (this.type === UIModal.Type.Confirmation) {
            updateElement(this.buttonBar.querySelector('button.-accept'), {
                events: {
                    click: this.handleAcceptAction,
                },
            });
            updateElement(this.buttonBar.querySelector('button.-decline'), {
                events: {
                    click: this.handleDeclineAction,
                },
            });
        }

        this.disableBodyScroll();
        this.modalBody.addEventListener('scroll', this.scrollShadowHander);

        const shouldSwipe =
            this.bottomSheet && this.isClosable() && !this.compact;
        if (shouldSwipe) {
            this.connectSwipeEvents();
        }
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        if (this.isActive()) {
            dispatchCustomEvent(document.body, 'modal-updatelist', {
                action: 'remove',
            });
        }
        this.modalBody.removeEventListener('scroll', this.scrollShadowHander);
        if (this.type !== UIModal.Type.Popover && this.backdrop) {
            this.backdrop.removeEventListener(
                'click',
                this.handleBackdropInteraction
            );
            this.backdrop.removeEventListener(
                'keydown',
                this.handleBackdropInteraction
            );
        }

        if (this.bottomSheet && this.isClosable() && !this.compact) {
            this.disconnectSwipeEvents();
        }
    }
}

UIModal.defineElement('ui-modal', styles);
export { UIModal };
